import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "./api/axios";
import { useActivityLogger } from "./hooks/useActivity";

export default function VideoDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [video, setVideo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // const [isSaved, setIsSaved] = useState(false);
  const [showPlayer, setShowPlayer] = useState(false);
  const { logUserActivity, loading: userLoading } = useActivityLogger();

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return "Tanggal tidak diketahui";

    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return "1 hari yang lalu";
    if (diffDays < 30) return `${diffDays} hari yang lalu`;
    if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} bulan yang lalu`;
    }
    const years = Math.floor(diffDays / 365);
    return `${years} tahun yang lalu`;
  };

  // Format duration helper
  const formatDuration = (duration) => {
    if (!duration) return "-";

    // Handle ISO 8601 duration format (PT15M33S)
    const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
    if (match) {
      const hours = parseInt(match[1]) || 0;
      const minutes = parseInt(match[2]) || 0;
      const seconds = parseInt(match[3]) || 0;

      if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
      } else {
        return `${minutes}:${seconds.toString().padStart(2, "0")}`;
      }
    }

    return duration;
  };

  // Parse tags from JSON
  const parseTags = (tagData) => {
    if (!tagData) return [];

    try {
      if (typeof tagData === "string") {
        return JSON.parse(tagData);
      }
      if (Array.isArray(tagData)) {
        return tagData;
      }
      return [];
    } catch (error) {
      console.error("Error parsing tags:", error);
      return [];
    }
  };

  // Extract YouTube video ID from various formats
  const getYouTubeVideoId = (video) => {
    const videoId = video.video_id || video.videoID || id;

    // If it's already a video ID (11 characters), return it
    if (videoId && videoId.length === 11) {
      return videoId;
    }

    // Extract from YouTube URL
    const url = video.videoUrl || video.video_url || videoId;
    if (url) {
      const regExp =
        /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
      const match = url.match(regExp);
      return match && match[2].length === 11 ? match[2] : null;
    }

    return null;
  };

  // Fetch video details
  useEffect(() => {
    const fetchVideoDetails = async () => {
      try {
        setLoading(true);
        setError(null);

        // Try to get detailed video info from YouTube API first
        let response;
        try {
          response = await api.get(`/youtube/videos/${id}/detail`);
        } catch (detailError) {
          // If detail API fails, fallback to regular video endpoint
          console.warn(
            "Detail API failed, falling back to regular video endpoint:",
            detailError
          );
          response = await api.get(`/youtube/videos/${id}`);
        }

        const videoData = response.data;
        setVideo(videoData);
        // setIsSaved(videoData.is_saved || false);

        // Log page view setelah berhasil fetch data
        if (videoData?.title && !userLoading) {
          logUserActivity(
            "video",
            "view_video_details",
            "video_details",
            `User viewed details for video: ${videoData.title}`
          );
        }
      } catch (err) {
        console.error("Failed to fetch video details:", err);
        setError("Gagal memuat detail video");
        if (!userLoading) {
          logUserActivity(
            "error",
            "fetch_video_details_failed",
            "video_details",
            `Failed to fetch video details for ID ${id}: ${err.message}`
          );
        }
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchVideoDetails();
    }
  }, [id, userLoading]);

  // Handle save/unsave video
  const handleToggleSave = async () => {
    try {
      const payload = {
        video_id: video.video_id || video.videoID || id,
        title: video.title,
        channel_title: video.channelTitle || video.channel_title,
        thumbnail_url: video.thumbnail || video.thumbnail_url,
      };

      const response = await api.post("/youtube/save", payload);
      // setIsSaved(response.data.is_saved);

      logUserActivity(
        "video",
        response.data.is_saved ? "save_video" : "unsave_video",
        "video_details",
        `User ${response.data.is_saved ? "saved" : "unsaved"} video: ${video.title}`
      );
    } catch (error) {
      console.error("Failed to toggle save status:", error);
      alert("Gagal mengubah status simpan video");
      logUserActivity(
        "error",
        "toggle_save_failed",
        "video_details",
        `Failed to toggle save status for video: ${video.title}`
      );
    }
  };

  // Handle play video (show embedded player)
  const handlePlayVideo = () => {
    setShowPlayer(true);
    logUserActivity(
      "video",
      "play_video",
      "video_details",
      `User started playing video: ${video.title}`
    );
  };

  // Handle close player
  const handleClosePlayer = () => {
    setShowPlayer(false);
    logUserActivity(
      "video",
      "close_video_player",
      "video_details",
      `User closed video player for: ${video.title}`
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white px-4 py-4 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
          <p className="text-gray-600">Memuat detail video...</p>
        </div>
      </div>
    );
  }

  if (error || !video) {
    return (
      <div className="min-h-screen bg-white px-4 py-4">
        <div className="flex items-center justify-between mb-4">
          <button onClick={() => navigate("/videos")}>
            <img src="/icon-back.png" alt="Back" className="h-6 w-6" />
          </button>
          <h1 className="text-xl font-semibold">Video</h1>
          <div className="h-6 w-6" />
        </div>
        <div className="text-center mt-20">
          <p className="text-red-600 mb-4">
            {error || "Video tidak ditemukan"}
          </p>
          <button
            onClick={() => navigate("/videos")}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg"
          >
            Kembali
          </button>
        </div>
      </div>
    );
  }

  const tags = parseTags(video.tag);
  const youtubeVideoId = getYouTubeVideoId(video);

  return (
    <div className="min-h-screen bg-white px-4 py-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => navigate("/videos")}>
          <img src="/icon-back.png" alt="Back" className="h-6 w-6" />
        </button>
        <h1 className="text-xl font-semibold">Video</h1>
        <div className="h-6 w-6" />
      </div>

      {/* Video Player or Thumbnail */}
      <div className="relative mb-4">
        {showPlayer && youtubeVideoId ? (
          <div className="relative">
            {/* Embedded YouTube Player */}
            <div
              className="relative w-full"
              style={{ paddingBottom: "56.25%" }}
            >
              <iframe
                className="absolute top-0 left-0 w-full h-full rounded-xl"
                src={`https://www.youtube.com/embed/${youtubeVideoId}?autoplay=1&rel=0&modestbranding=1`}
                title={video.title}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            {/* Close Player Button */}
            <button
              onClick={handleClosePlayer}
              className="absolute top-2 right-2 bg-black/70 text-white rounded-full p-2 hover:bg-black/90 transition-colors z-10"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        ) : (
          <div className="relative">
            <img
              src={video.thumbnail || "/video-details1.png"}
              alt="Thumbnail"
              className="w-full rounded-xl"
              onError={(e) => {
                e.target.src = "/video-details1.png";
              }}
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <button onClick={handlePlayVideo}>
                <img
                  src="/icon-play.png"
                  alt="Play"
                  className="h-14 w-14 opacity-90 hover:opacity-100 transition-opacity"
                />
              </button>
            </div>
            <span className="absolute bottom-2 right-2 text-xs bg-black/70 text-white px-2 py-0.5 rounded">
              {formatDuration(video.duration || video.durasi)}
            </span>
          </div>
        )}
      </div>

      {/* Title and Info */}
      <div className="flex items-start justify-between mb-1">
        <h2 className="text-base font-semibold flex-1 pr-2">{video.title}</h2>
        {/* <button onClick={handleToggleSave}>
          <img
            src={isSaved ? "/icon-bookmark-filled.png" : "/icon-bookmark.png"}
            alt="Bookmark"
            className="h-6 w-6 mt-0.5"
          />
        </button> */}
      </div>
      <p className="text-sm text-gray-500">
        {video.channelTitle ||
          video.channel_title ||
          video.sumber ||
          "Unknown Source"}{" "}
        • {formatDate(video.publishedAt)}
      </p>

      {/* Description */}
      <p
        className="text-sm text-gray-800 mt-4 leading-relaxed"
        dangerouslySetInnerHTML={{
          __html: video.description || "Tidak ada deskripsi tersedia.",
        }}
      ></p>

      {/* Tags */}
      {tags.length > 0 && (
        <div className="mt-6 text-sm flex items-center gap-3">
          <span className="font-medium text-gray-800">Tag:</span>
          <div className="flex gap-2 flex-wrap">
            {tags.map((tag, index) => (
              <span
                key={index}
                className="bg-gray-100 text-blue-500 px-3 py-1 rounded-md text-xs font-medium"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* External Link Option */}
      {youtubeVideoId && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <button
            onClick={() => {
              const videoUrl = `https://www.youtube.com/watch?v=${youtubeVideoId}`;
              window.open(videoUrl, "_blank");
              logUserActivity(
                "video",
                "open_external_youtube",
                "video_details",
                `User opened video in YouTube: ${video.title}`
              );
            }}
            className="flex items-center gap-2 text-red-600 text-sm font-medium hover:text-red-700"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
            </svg>
            Buka di YouTube
          </button>
        </div>
      )}
    </div>
  );
}
